import classnames from 'classnames'
import * as React from 'react'

import type { UTMParameters } from '@/lib/hooks/useUrlWithUtmParameters.ts'
import { SEO_SPACER } from '@/lib/seoSpacer.ts'
import NordicLink from '../Button/NordicLink.tsx'
import styles from './BenefitsSummary.module.scss'
import type { IconProps } from './Icons.tsx'

export type Benefit = {
	icon: React.FC<IconProps>
	title: string
	copy: React.ReactNode
}

export type BenefitsSummaryCta = {
	label: string
	href: string
	utmParams?: UTMParameters
}

export type BenefitsSummaryProps = {
	heading: React.ReactNode
	benefits: Benefit[]
	cta?: BenefitsSummaryCta
	iconColor?: string
	iconBackgroundColor?: string
	className?: string
}

export const BenefitsSummary: React.FC<BenefitsSummaryProps> = ({
	heading,
	benefits,
	cta,
	iconColor,
	iconBackgroundColor,
	className,
}) => (
	<section className={classnames(styles.benefitsSummary, className)}>
		<h2 className={styles.heading}>
			{heading}
			{SEO_SPACER}
		</h2>
		<ul className={styles.stack}>
			{benefits.map(({ title, copy, icon: Icon }) => (
				<li key={title} className={styles.card}>
					<span
						className={styles.iconWrap}
						style={
							iconBackgroundColor
								? { backgroundColor: iconBackgroundColor }
								: undefined
						}
					>
						<Icon color={iconColor} aria-hidden focusable={false} />
					</span>
					<div className={styles.copy}>
						<h3>
							{title}
							{SEO_SPACER}
						</h3>
						<p>
							{copy}
							{SEO_SPACER}
						</p>
					</div>
				</li>
			))}
		</ul>
		{cta && (
			<NordicLink
				showSlant
				href={cta.href}
				size="large"
				className={styles.cta}
				preserveUtmParams
				utmParams={cta.utmParams}
				data-testid="benefits-cta"
			>
				{cta.label}
				{SEO_SPACER}
			</NordicLink>
		)}
	</section>
)
